import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Layout from "./Layout";
import DateInput from "../components/DateInput";
import usePageMeta from "../hooks/usePageMeta";
import { MdEmail } from "react-icons/md";
import { FaLock } from "react-icons/fa";
import ResetPasswordForm from "../components/forms/ResetPasswordForm";
import TrustedDevicesForm from "../components/forms/TrustedDevicesForm";
import TwoStepVerificationSection from "../components/twoFactor/TwoStepVerificationSection";
import StaffAvatarCircle from "../components/StaffAvatarCircle";
import { BASE_URL } from "../config";
import useAuth from "../hooks/useAuth";
import 'react-phone-number-input/style.css';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { getOsIcon, getBrowserIcon, getDeviceTypeIcon, formatDeviceInfo } from "../utils/deviceIcons";
import { showSuccess, showError, showConfirm } from '../utils/dialog';
import { formatAppDateTime } from '../utils/appDateTime';
import {
  STAFF_DOCUMENT_ACCEPT,
  validateStaffDocumentFile,
} from '../utils/staffDocumentValidation';
import AddressAutocomplete from "../components/AddressAutocomplete";
import {
  AtlasPageHeader,
  AtlasPageDivider,
  AtlasTabBar,
} from "../components/page-layout";

const MY_ACCOUNT_TABS = [
  "GENERAL INFORMATION",
  "CONTACT",
  "PAYROLL",
  "SIGN IN & SECURITY",
];

// Residency mapping helpers
const mapServerResidencyToUI = (val) => {
  if (!val && val !== "") return val;
  const s = String(val || "").trim();
  switch (s) {
    case "study_resident":
    case "study_permit":
    case "study_res":
      return "study_permit";
    case "temporary_resident":
    case "work_permit":
    case "temp_res":
      return "work_permit";
    case "other":
    case "refugee":
      return "refugee";
    case "citizen":
    case "canadian":
      return "canadian";
    default:
      return s;
  }
};

const mapUIResidencyToServer = (val) => {
  if (!val && val !== "") return val;
  const s = String(val || "").trim();
  switch (s) {
    case "study_permit":
      return "study_resident";
    case "work_permit":
      return "temporary_resident";
    case "refugee":
      return "other";
    case "canadian":
      return "citizen";
    default:
      return s;
  }
};

const normalizeAddressValue = (value) => {
  if (value && typeof value === "object") {
    return {
      street: value.street ?? "",
      address2: value.address2 ?? value.line2 ?? "",
      city: value.city ?? "",
      region: value.region ?? value.state ?? "",
      zip: value.zip ?? value.postal_code ?? "",
      country: value.country ?? "",
    };
  }

  return {
    street: typeof value === "string" ? value : "",
    address2: "",
    city: "",
    region: "",
    zip: "",
    country: "",
  };
};

const MyAccount = () => {
  usePageMeta("MyAccount");

  const [selectedTab, setSelectedTab] = useState("GENERAL INFORMATION");
  const [openModal, setOpenModal] = useState(null);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showTrustedModal, setShowTrustedModal] = useState(false);
  const [securityData, setSecurityData] = useState({
    email: "",
    last_signin: "",
    two_factor_enabled: false,
    devices: []
  });
  const auth = useAuth();
  // Show only the most recent few devices in the summary list
  const recentDevices = (securityData.devices || []).slice(0, 3);
  const totalDevices = (securityData.devices || []).length;

  // Original saved data from backend
  const [originalData, setOriginalData] = useState({});

  // Form state – mapped to backend fields (for editing)
  const [form, setForm] = useState({
    preferred_name: "",
    birthdate: "",
    first_name: "",
    last_name: "",
    middle_name: "",
    email: "",
    gender: "",
    phone_mobile: "",
    street: "",
    address2: "",
    city: "",
    region: "",
    zip: "",
    country: "",
    emergency_name: "",
    emergency_phone: "",
    personal_number: "",
    personal_email: "",
    residency_status: "",
    hst_number: "",
    cheque_method: "",
    sin: "",
    signin_date: "",
    google_2fa_secret_key: "",
    force_2fa: "",
    profile_picture: "",
    residency_document: "",
    residency_document_name: "",
    residency_document_url: ""
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_mobile: "",
    street: "",
    emergency_name: "",
    emergency_phone: "",
    residency_status: "",
    residency_document: "",
    sin: ""
  });

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target?.value }));
    // Clear error when user starts typing
    if (formErrors[key]) {
      setFormErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const hasAddressValue = () =>
    Boolean(
      String(form.street || "").trim() ||
      String(form.address2 || "").trim() ||
      String(form.city || "").trim() ||
      String(form.region || "").trim() ||
      String(form.zip || "").trim() ||
      String(form.country || "").trim()
    );

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    const fieldsToCheck = [
      'first_name', 'last_name', 'middle_name', 'preferred_name', 'email', 'gender', 'birthdate',
      'phone_mobile', 'personal_number', 'personal_email', 'emergency_name', 'emergency_phone',
      'hst_number', 'cheque_method', 'sin', 'residency_status', 'street', 'address2', 'city', 'region', 'zip', 'country'
    ];
    return fieldsToCheck.some(field => form[field] !== originalData[field]);
  };

  const handleCancel = () => {
    setForm(originalData);
  };

  const loadSecurityData = async () => {
    try {
      console.log('🔍 Loading security data...');
      const accountId = auth?.accountId || localStorage.getItem("accountId");
      const token = auth?.token || localStorage.getItem("token");
      console.log('🔑 Using accountId:', accountId, 'token:', token ? 'present' : 'missing');

      const url = `${BASE_URL}/api/my-account/account/security${accountId ? `?accountId=${accountId}` : ""}`;
      console.log('🌐 Fetching from:', url);

      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      const j = await res.json();
      console.log('📊 Security data response:', j);

      if ((res.ok || j?.success) && j?.data) {
        setSecurityData(j.data);
        console.log('✅ Security data loaded:', j.data);
        console.log('📱 Devices data:', j.data.devices);
        if (j.data.devices && j.data.devices.length > 0) {
          console.log('🔍 Sample device:', JSON.stringify(j.data.devices[0], null, 2));
        }
      } else {
        console.warn('⚠️ Security data load failed:', j);
      }
    } catch (e) {
      console.error("❌ Security data load error", e);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const accountId = auth?.accountId || localStorage.getItem("accountId");
        const token = auth?.token || localStorage.getItem("token");
        const url = `${BASE_URL}/api/my-account/account/me${accountId ? `?accountId=${accountId}` : ""}`;
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });
        const j = await res.json();
        if ((res.ok || j?.success) && j?.data) {
          const d = j.data;
          const userData = {
            preferred_name: d.preferred_name || "",
            birthdate: d.birthdate ? d.birthdate.slice(0, 10) : "",
            first_name: d.first_name || "",
            last_name: d.last_name || "",
            middle_name: d.middle_name || "",
            email: d.email || "",
            gender: d.gender || "",
            phone_mobile: d.phone_mobile || "",
            street: d.street || "",
            address2: d.address2 || "",
            city: d.city || "",
            region: d.region || "",
            zip: d.zip || "",
            country: d.country || "",
            emergency_name: d.emergency_name || "",
            emergency_phone: d.emergency_phone || "",
            personal_number: d.personal_number || "",
            personal_email: d.personal_email || "",
            residency_status: mapServerResidencyToUI(d.residency_status || ""),
            hst_number: d.hst_number || "",
            cheque_method: d.cheque_method || "",
            sin: d.sin || "",
            signin_date: d.signin_date || "",
            google_2fa_secret_key: d.google_2fa_secret_key || "",
            force_2fa: d.force_2fa || "",
            profile_picture: d.profile_picture || "",
            residency_document: d.work_permit_file_id || d.study_permit_file_id || d.refugee_file_id || "",
            residency_document_name: "",
            residency_document_url: ""
          };
          setOriginalData(userData);
          setForm(userData);

          // If there's a residency document, resolve its URL
          const fileId = userData.residency_document;
          if (fileId) {
            try {
              const fileRes = await fetch(`${BASE_URL}/api/registration/file/${fileId}/url`, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined
              });
              const fileJson = await fileRes.json();
              if (fileRes.ok && fileJson.url) {
                const url = fileJson.url;
                const name = fileJson.name || fileJson.filename || url.split('/').pop() || "Document";
                setForm(prev => ({ ...prev, residency_document_url: url, residency_document_name: name }));
                setOriginalData(prev => ({ ...prev, residency_document_url: url, residency_document_name: name }));
              }
            } catch (fe) {
              console.warn("Failed to load residency document URL", fe);
            }
          }
        } else {
          console.warn("MyAccount: no profile data", j);
        }

        // Load security data
        await loadSecurityData();
      } catch (e) {
        console.error("MyAccount load error", e);
      } finally {
        setLoading(false);
      }
    };
    // Only attempt when we have at least accountId or token ready
    if (auth || localStorage.getItem("accountId") || localStorage.getItem("token")) {
      load();
    }
  }, [auth?.accountId, auth?.token]);

  const onSave = async () => {
    // Validation: Check required fields
    const errors = {};
    let hasErrors = false;

    if (selectedTab === "GENERAL INFORMATION") {
      if (!form.first_name || form.first_name.trim() === '') {
        errors.first_name = 'First Name is required';
        hasErrors = true;
      }

      if (!form.last_name || form.last_name.trim() === '') {
        errors.last_name = 'Last Name is required';
        hasErrors = true;
      }

      if (!form.email || form.email.trim() === '') {
        errors.email = 'Email is required';
        hasErrors = true;
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(form.email.trim())) {
          errors.email = 'Please enter a valid email address';
          hasErrors = true;
        }
      }
    }

    if (selectedTab === "CONTACT") {
      if (!form.phone_mobile || String(form.phone_mobile).trim() === '') {
        errors.phone_mobile = 'Mobile Number is required';
        hasErrors = true;
      }
      if (!hasAddressValue()) {
        errors.street = 'Address is required';
        hasErrors = true;
      }
      if (!form.emergency_name || String(form.emergency_name).trim() === '') {
        errors.emergency_name = 'Emergency Contact Name is required';
        hasErrors = true;
      }
      if (!form.emergency_phone || String(form.emergency_phone).trim() === '') {
        errors.emergency_phone = 'Emergency Contact Number is required';
        hasErrors = true;
      }
    }

    if (selectedTab === "PAYROLL") {
      if (!form.residency_status || String(form.residency_status).trim() === '') {
        errors.residency_status = 'Residency Status is required';
        hasErrors = true;
      }
      if (
        ['work_permit', 'study_permit', 'refugee'].includes(form.residency_status) &&
        !form.residency_document &&
        !form.residency_document_url &&
        !form.residency_document_name
      ) {
        errors.residency_document = 'Residency document is required';
        hasErrors = true;
      }
      if (!form.sin || String(form.sin).trim() === '') {
        errors.sin = 'Social Insurance Number is required';
        hasErrors = true;
      }
    }

    // Show errors if any
    if (hasErrors) {
      setFormErrors(errors);
      showError('Please fill the required fields before saving');
      setSaving(false);
      return;
    }

    // Clear errors if validation passes
    setFormErrors({
      first_name: "",
      last_name: "",
      email: "",
      phone_mobile: "",
      street: "",
      emergency_name: "",
      emergency_phone: "",
      residency_status: "",
      residency_document: "",
      sin: ""
    });

    try {
      setSaving(true);
      const accountId = auth?.accountId || localStorage.getItem("accountId");
      const token = auth?.token || localStorage.getItem("token");
      let payload = {};
      let savedData = {};

      if (selectedTab === "GENERAL INFORMATION") {
        payload = {
          first_name: form.first_name,
          last_name: form.last_name,
          middle_name: form.middle_name,
          preferred_name: form.preferred_name,
          email: form.email,
          gender: form.gender,
          birthdate: form.birthdate || null,
        };
        savedData = {
          first_name: form.first_name,
          last_name: form.last_name,
          middle_name: form.middle_name,
          preferred_name: form.preferred_name,
          email: form.email,
          gender: form.gender,
          birthdate: form.birthdate,
        };
      } else if (selectedTab === "CONTACT") {
        payload = {
          phone_mobile: form.phone_mobile,
          personal_number: form.personal_number,
          personal_email: form.personal_email,
          emergency_name: form.emergency_name,
          emergency_phone: form.emergency_phone,
          street: form.street,
          address2: form.address2,
          city: form.city,
          region: form.region,
          zip: form.zip,
          country: form.country,
        };
        savedData = {
          phone_mobile: form.phone_mobile,
          personal_number: form.personal_number,
          personal_email: form.personal_email,
          emergency_name: form.emergency_name,
          emergency_phone: form.emergency_phone,
          street: form.street,
          address2: form.address2,
          city: form.city,
          region: form.region,
          zip: form.zip,
          country: form.country,
        };
      } else if (selectedTab === "PAYROLL") {
        payload = {
          hst_number: form.hst_number,
          cheque_method: form.cheque_method,
          sin: form.sin,
          residency_status: mapUIResidencyToServer(form.residency_status),
        };
        savedData = {
          hst_number: form.hst_number,
          cheque_method: form.cheque_method,
          sin: form.sin,
          residency_status: form.residency_status,
        };
      }

      const res = await fetch(`${BASE_URL}/api/my-account/account/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(accountId ? { ...payload, accountId } : payload)
      });
      const j = await res.json();
      if (!j?.success) throw new Error(j?.message || "Save failed");

      // Update original data to reflect saved state
      setOriginalData(prev => ({ ...prev, ...savedData }));
      showSuccess('Profile updated successfully');
    } catch (e) {
      console.error("MyAccount save error", e);
      showError(e.message || 'Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async ({ currentPassword, newPassword }) => {
    try {
      const token = auth?.token || localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/api/my-account/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.success) {
        throw new Error(j?.message || "Password reset failed");
      }
      showSuccess(j.message || "Password updated successfully");
      setOpenModal(null);
    } catch (e) {
      console.error("Password reset error", e);
      showError(e.message || "Failed to update password");
      throw e;
    }
  };

  const handleRemoveDevice = async (deviceId, skipConfirm = false) => {
    if (!skipConfirm) {
      if (!(await showConfirm("Are you sure you want to remove this trusted device?"))) return;
    }

    try {
      const accountId = auth?.accountId || localStorage.getItem("accountId");
      const token = auth?.token || localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/api/my-account/account/security/device/${deviceId}?accountId=${accountId}`, {
        method: "DELETE",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      const j = await res.json();
      if (!j?.success) throw new Error(j?.message || "Device removal failed");

      // Update local state
      setSecurityData(prev => ({
        ...prev,
        devices: prev.devices.filter(d => d.trusted_device_id !== deviceId)
      }));
      showSuccess("Device removed successfully");
    } catch (e) {
      console.error("Device removal error", e);
      showError("Failed to remove device");
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setSaving(true);
      const token = auth?.token || localStorage.getItem("token");
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${BASE_URL}/api/my-account/account/avatar`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: formData
      });

      const json = await res.json();
      if (json.success && json.url) {
        // Reload user profile data to get the updated profile_picture URL
        const accountId = auth?.accountId || localStorage.getItem("accountId");
        const profileRes = await fetch(`${BASE_URL}/api/my-account/account/me${accountId ? `?accountId=${accountId}` : ""}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });
        const profileJson = await profileRes.json();

        if (profileJson.success && profileJson.data) {
          setForm(prev => ({
            ...prev,
            profile_picture: profileJson.data.profile_picture || json.url
          }));
        } else {
          // Fallback to the direct URL from upload response
          setForm(prev => ({ ...prev, profile_picture: json.url }));
        }

        showSuccess('Profile picture updated successfully');
      } else {
        showError(json.message || 'Failed to upload profile picture');
      }
    } catch (err) {
      console.error('Avatar upload error:', err);
      showError('Failed to upload profile picture');
    } finally {
      setSaving(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleResidencyFile = async (file) => {
    if (!file) return;
    const validationMessage = validateStaffDocumentFile(file);
    if (validationMessage) return showError(validationMessage);
    const accountId = auth?.accountId || localStorage.getItem("accountId");
    if (!accountId) return showError("Account not found");

    const docType = form.residency_status;
    if (!['work_permit', 'study_permit', 'refugee'].includes(docType)) {
      return showError("Please select a valid Residency Status before uploading (Work Permit, Study Permit, or Refugee)");
    }

    const fd = new FormData();
    fd.append("file", file);
    fd.append("docType", docType);

    try {
      setSaving(true);
      const token = auth?.token || localStorage.getItem("token");
      const headers = {
        'Content-Type': 'multipart/form-data',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const resp = await axios.post(
        `${BASE_URL}/api/registration/${accountId}/upload-and-link`,
        fd,
        { headers }
      );

      const data = resp?.data || {};
      if (!data || !data.file_id) {
        throw new Error(data?.message || "Upload failed");
      }

      setForm((prev) => ({
        ...prev,
        residency_document: data.file_id,
        residency_document_name: file.name,
        residency_document_url: data.url || data.s3Key || "",
      }));
      if (formErrors.residency_document) {
        setFormErrors((prev) => ({ ...prev, residency_document: "" }));
      }
      showSuccess("File uploaded successfully");
    } catch (err) {
      console.error("Residency upload failed", err);
      showError(err.message || "File upload failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const removeResidencyFile = async () => {
    const fileId = form.residency_document;
    if (!fileId) return;

    if (!await showConfirm("Are you sure you want to remove this document?")) return;

    try {
      setSaving(true);
      const accountId = auth?.accountId || localStorage.getItem("accountId");
      const token = auth?.token || localStorage.getItem("token");

      await axios.delete(`${BASE_URL}/api/registration/${accountId}/files/${fileId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setForm(prev => ({
        ...prev,
        residency_document: "",
        residency_document_name: "",
        residency_document_url: ""
      }));
      showSuccess("Document removed successfully");
    } catch (err) {
      console.error("Error removing residency file", err);
      showError("Failed to remove document");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout active="MyAccount">
      <style jsx>{`
      .PhoneInput {
        border: 1px solid rgba(0, 0, 0, 0.2);
        border-radius: 8px;
        padding: 8px 12px;
        background: white;
        height: 40px;
        display: flex;
        align-items: center;
      }
      .PhoneInput:focus-within {
        outline: none;
        border-color: #DC1F00;
        box-shadow: 0 0 0 1px #DC1F00;
      }
      .PhoneInputInput {
        border: none;
        outline: none;
        background: transparent;
        font-size: 14px;
        padding: 0;
        margin-left: 8px;
        flex: 1;
      }
      .PhoneInputCountrySelect {
        border: none;
        background: transparent;
        outline: none;
        font-size: 14px;
        margin-right: 8px;
      }
    `}</style>
      <div className="py-3 px-2 flex flex-col min-h-screen">
        <AtlasPageHeader title="My Account" showBack={false} />
        <AtlasPageDivider />

        <div className="mt-6 mb-4 mx-2 sm:mx-6 p-2 sm:p-4 border-2 border-gray-200 rounded-xl h-full">
          <AtlasTabBar
            tabs={MY_ACCOUNT_TABS}
            activeTab={selectedTab}
            onTabChange={setSelectedTab}
          />

          {/* GENERAL INFORMATION section */}
          {selectedTab === "GENERAL INFORMATION" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* first column */}
              <div className="p-4">
                <label className="block text-sm text-[#00000080] mb-2">Profile Picture</label>
                <div className="relative w-16 h-16 mb-4">
                  {/* Profile Circle */}
                  <StaffAvatarCircle
                    accountId={auth?.accountId || localStorage.getItem("accountId")}
                    employee={{
                      account_id: auth?.accountId || localStorage.getItem("accountId"),
                      profile_picture: form.profile_picture,
                      name:
                        [form.first_name, form.last_name].filter(Boolean).join(" ").trim() ||
                        form.preferred_name,
                    }}
                    className="w-16 h-16 border-2 border-gray-300"
                    textClassName="text-base font-semibold text-gray-700"
                    emptyLabel="NA"
                  />
                  {/* Camera Icon Overlay */}
                  <label className="absolute bottom-0 right-0 bg-red-600 w-6 h-6 rounded-full flex items-center justify-center cursor-pointer border-2 border-white hover:bg-red-700">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      className="hidden"
                      disabled={saving}
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h4l3-3h4l3 3h4v12H3V7z" />
                    </svg>
                  </label>
                </div>
                <label className="block text-sm text-[#00000080] mb-2">Preffered Name</label>
                <input
                  type="text"
                  name="name"
                  value={form.preferred_name}
                  onChange={handleChange('preferred_name')}
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 focus:ring-1 focus:ring-red-400"
                />
                <p className="text-xs text-[#00000080] mt-1 mb-4">
                  Only if different from First Name
                </p>
                <label className="block text-sm text-[#00000080] mb-2">Date of Birth</label>
                <DateInput
                  name="dob"
                  value={form.birthdate}
                  onChange={(value) => handleChange('birthdate')({ target: { value } })}
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
              </div>

              {/* second column */}
              <div className="p-4 mt-1 lg:mt-6">
                <label className="block text-sm text-[#00000080] mb-2">First Name <span className="text-[#00000080]">*</span></label>
                <input
                  type="text"
                  name="name"
                  value={form.first_name}
                  onChange={handleChange('first_name')}
                  required
                  className={`w-full border rounded-lg px-2 py-2 mb-1 focus:ring-1 focus:ring-red-400 ${formErrors.first_name ? 'border-red-500' : 'border-[#00000033]'
                    }`}
                />
                {formErrors.first_name && (
                  <div className="text-xs text-red-600 mb-3">{formErrors.first_name}</div>
                )}
                <label className="block text-sm text-[#00000080] mb-2">Last Name <span className="text-[#00000080]">*</span></label>
                <input
                  type="text"
                  name="lastName"
                  value={form.last_name}
                  onChange={handleChange('last_name')}
                  required
                  className={`w-full border rounded-lg px-2 py-2 mb-1 focus:ring-1 focus:ring-red-400 ${formErrors.last_name ? 'border-red-500' : 'border-[#00000033]'
                    }`}
                />
                {formErrors.last_name && (
                  <div className="text-xs text-red-600 mb-3">{formErrors.last_name}</div>
                )}
                <label className="block text-sm text-[#00000080] mb-2">Middle Name</label>
                <input
                  type="text"
                  name="middleName"
                  value={form.middle_name}
                  onChange={handleChange('middle_name')}
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
              </div>

              {/* third column */}
              <div className="p-4 mt-1 lg:mt-6">
                <label className="block text-sm text-[#00000080] mb-2">Email <span className="text-[#00000080]">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  required
                  className={`w-full border rounded-lg px-2 py-2 mb-1 focus:ring-1 focus:ring-red-400 ${formErrors.email ? 'border-red-500' : 'border-[#00000033]'
                    }`}
                />
                {formErrors.email && (
                  <div className="text-xs text-red-600 mb-3">{formErrors.email}</div>
                )}
                <label className="block text-sm text-[#00000080] mb-2">Gender</label>
                <select name="Gender" required className="w-full border border-[#00000033] rounded-lg px-3 py-2 mb-4 focus:ring-1 focus:ring-red-400" value={form.gender || ''} onChange={handleChange('gender')}>
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          )}

          {/* CONTACT section */}
          {selectedTab === "CONTACT" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* first column */}
              <div className="p-6">
                <label className="block text-sm text-[#00000080] mb-2">Mobile Number <span className="text-[#00000080]">*</span></label>
                <div className="mb-6">
                  <PhoneInput
                    value={form.phone_mobile}
                    onChange={(value) => {
                      setForm(prev => ({ ...prev, phone_mobile: value }));
                      if (formErrors.phone_mobile) {
                        setFormErrors((prev) => ({ ...prev, phone_mobile: "" }));
                      }
                    }}
                    defaultCountry="CA"
                    international
                    withCountryCallingCode
                    countryCallingCodeEditable={false}
                  />
                  {formErrors.phone_mobile && <div className="text-red-600 text-xs mt-1">{formErrors.phone_mobile}</div>}
                </div>
                <label className="block text-sm text-[#00000080] mb-2">Address <span className="text-[#00000080]">*</span></label>
                <AddressAutocomplete
                  value={(() => {
                    const parts = [];
                    const normalizedAddress = normalizeAddressValue(form.street);
                    const streetVal = normalizedAddress.street;
                    if (streetVal) parts.push(streetVal);
                    if (form.address2 || normalizedAddress.address2) parts.push(form.address2 || normalizedAddress.address2);
                    if (form.city || normalizedAddress.city) parts.push(form.city || normalizedAddress.city);
                    if (form.region || normalizedAddress.region) parts.push(form.region || normalizedAddress.region);
                    if (form.zip || normalizedAddress.zip) parts.push(form.zip || normalizedAddress.zip);
                    if (form.country || normalizedAddress.country) parts.push(form.country || normalizedAddress.country);
                    return parts.join(', ');
                  })()}
                  onChange={(v) => {
                    if (v && typeof v === 'object') {
                      setForm(prev => ({
                        ...prev,
                        street: v.street || '',
                        address2: v.address2 || prev.address2 || '',
                        city: v.city || '',
                        region: v.region || '',
                        zip: v.zip || '',
                        country: v.country || ''
                      }));
                    } else {
                      const nextStreet = typeof v === 'string' ? v : '';
                      if (nextStreet) {
                        setForm(prev => ({ ...prev, street: nextStreet }));
                      } else {
                        setForm(prev => ({
                          ...prev,
                          street: '',
                          address2: '',
                          city: '',
                          region: '',
                          zip: '',
                          country: ''
                        }));
                      }
                    }
                    if (formErrors.street) {
                      setFormErrors((prev) => ({ ...prev, street: "" }));
                    }
                  }}
                />
                {formErrors.street && <div className="text-red-600 text-xs mt-1">{formErrors.street}</div>}
              </div>

              {/* second column */}
              <div className="p-6">
                <label className="block text-sm text-[#00000080] mb-2">Emergency Contact Name <span className="text-[#00000080]">*</span></label>
                <input
                  type="text"
                  name="emergencyContactName"
                  value={form.emergency_name}
                  onChange={handleChange('emergency_name')}
                  required
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-6 focus:ring-1 focus:ring-red-400"
                />
                {formErrors.emergency_name && <div className="text-red-600 text-xs -mt-4 mb-4">{formErrors.emergency_name}</div>}
                <label className="block text-sm text-[#00000080] mb-2">Emergency Contact No <span className="text-[#00000080]">*</span></label>
                <div className="mb-6">
                  <PhoneInput
                    value={form.emergency_phone}
                    onChange={(value) => {
                      setForm(prev => ({ ...prev, emergency_phone: value }));
                      if (formErrors.emergency_phone) {
                        setFormErrors((prev) => ({ ...prev, emergency_phone: "" }));
                      }
                    }}
                    defaultCountry="CA"
                    international
                    withCountryCallingCode
                    countryCallingCodeEditable={false}
                  />
                  {formErrors.emergency_phone && <div className="text-red-600 text-xs mt-1">{formErrors.emergency_phone}</div>}
                </div>
              </div>

              {/* third column */}
              <div className="p-6">
                <label className="block text-sm text-[#00000080] mb-2">Personal No</label>
                <div className="mb-6">
                  <PhoneInput
                    value={form.personal_number}
                    onChange={(value) => setForm(prev => ({ ...prev, personal_number: value }))}
                    defaultCountry="CA"
                    international
                    withCountryCallingCode
                    countryCallingCodeEditable={false}
                  />
                </div>
                <label className="block text-sm text-[#00000080] mb-2">Personal Email</label>
                <input
                  type="email"
                  name="PersonalEmail"
                  value={form.personal_email}
                  onChange={handleChange('personal_email')}
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-6 focus:ring-1 focus:ring-red-400"
                />
              </div>
            </div>
          )}

          {/* PAYROLL section */}
          {selectedTab === "PAYROLL" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
              {/* first column */}
              <div className="p-4">
                <label className="block text-sm text-[#00000080] mb-2">Residency Status <span className="text-[#00000080]">*</span></label>
                <select
                  name="Status"
                  required
                  className={`w-full border rounded-lg px-3 py-2 mb-1 focus:ring-1 focus:ring-red-400 ${formErrors.residency_status ? 'border-red-500' : 'border-[#00000033]'}`}
                  value={form.residency_status || ''}
                  onChange={(e) => {
                    handleChange('residency_status')(e);
                    if (formErrors.residency_document) {
                      setFormErrors((prev) => ({ ...prev, residency_document: "" }));
                    }
                  }}
                >
                  <option value="">Select</option>
                  <option value="canadian">Canadian Citizen / Permanent Resident</option>
                  <option value="work_permit">Work permit</option>
                  <option value="study_permit">Study permit</option>
                  <option value="refugee">Refugee</option>
                </select>
                {formErrors.residency_status && <div className="text-red-600 text-xs mt-1 mb-3">{formErrors.residency_status}</div>}

                {/* Residency document upload box */}
                {['work_permit', 'study_permit', 'refugee'].includes(form.residency_status) && (
                  <div className="mt-3">
                    <div
                      className={`border-2 border-dashed rounded-xl p-4 text-center bg-gray-50/50 ${formErrors.residency_document ? 'border-red-500' : 'border-gray-200'}`}
                      onDrop={(e) => {
                        e.preventDefault();
                        const f = e.dataTransfer.files && e.dataTransfer.files[0];
                        if (f) handleResidencyFile(f);
                      }}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      <p className="text-xs text-gray-500 mb-2 font-medium">
                        {form.residency_status === 'work_permit' ? 'Work Permit' : form.residency_status === 'study_permit' ? 'Study Permit' : 'Refugee Document'}
                      </p>
                      <div className="text-xs text-gray-400 mb-3">
                        {form.residency_document_url ? (
                          <div className="flex items-center justify-center gap-2">
                            <a
                              href={form.residency_document_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-red-600 underline truncate max-w-[150px]"
                            >
                              {form.residency_document_name || "View Document"}
                            </a>
                            <button
                              type="button"
                              onClick={removeResidencyFile}
                              className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-red-600"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          "Drag & drop file or click upload"
                        )}
                      </div>
                      <input
                        type="file"
                        id="residencyFileAdmin"
                        className="hidden"
                        accept={STAFF_DOCUMENT_ACCEPT}
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          if (f) handleResidencyFile(f);
                        }}
                        ref={fileInputRef}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-red-600 hover:bg-red-700 text-white text-[10px] px-3 py-1 rounded-full transition-colors"
                      >
                        UPLOAD
                      </button>
                    </div>
                    {formErrors.residency_document && <div className="text-red-600 text-xs mt-1">{formErrors.residency_document}</div>}
                  </div>
                )}
                <label className="block text-sm text-[#00000080] mb-2">HST Number</label>
                <input
                  type="text"
                  name="hstNo"
                  value={form.hst_number}
                  onChange={handleChange('hst_number')}
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
              </div>

              {/* second column */}
              <div className="p-4">
                <label className="block text-sm text-[#00000080] mb-2">Payment Delivery Method</label>
                <div className="flex items-center gap-8">
                  <label className="flex items-center gap-1 text-sm text-[#00000080]">
                    <input type="radio" name="paymentMethod" className="w-4 h-4 accent-[#DC1F00] mr-2" checked={form.cheque_method === 'delivery_pickup'} onChange={() => setForm(f => ({ ...f, cheque_method: 'delivery_pickup' }))} />
                    Pick Up
                  </label>
                  <label className="flex items-center gap-1 text-sm text-[#00000080]">
                    <input type="radio" name="paymentMethod" className="w-4 h-4 accent-[#DC1F00] mr-2" checked={form.cheque_method === 'delivery_mail'} onChange={() => setForm(f => ({ ...f, cheque_method: 'delivery_mail' }))} />
                    Mail
                  </label>
                </div>
              </div>

              {/* third column */}
              <div className="p-4">
                <label className="block text-sm text-[#00000080] mb-2">Social Insurance NO <span className="text-[#00000080]">*</span></label>
                <input
                  type="text"
                  name="socialNo"
                  value={form.sin}
                  onChange={handleChange('sin')}
                  required
                  className={`w-full border rounded-lg px-2 py-2 mb-1 focus:ring-1 focus:ring-red-400 ${formErrors.sin ? 'border-red-500' : 'border-[#00000033]'}`}
                />
                {formErrors.sin && <div className="text-red-600 text-xs mt-1">{formErrors.sin}</div>}
              </div>
            </div>
          )}

          {/* SIGN IN & SECURITY section */}
          {selectedTab === "SIGN IN & SECURITY" && (
            <div className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-2 mb-6">
                <div className="flex items-center gap-2">
                  <MdEmail className="text-[#00000080] text-lg shrink-0" />
                  <span className="font-medium text-[#00000080] shrink-0">Email:</span>
                  <span className="text-[#000000] break-all">{securityData.email || "Not available"}</span>
                </div>

                <div className="flex items-center gap-2 sm:ml-8">
                  <FaLock className="text-[#00000080] text-lg shrink-0" />
                  <span className="font-medium text-[#00000080] shrink-0">Password:</span>
                  <button
                    onClick={() => setOpenModal("reset")}
                    className="bg-[#DC1F0033] text-[#DC1F00] px-4 py-1 rounded-3xl hover:bg-[#CC2100] hover:text-white text-sm whitespace-nowrap"
                  >
                    Reset Password
                  </button>
                </div>

                {openModal === "reset" && (
                  <ResetPasswordForm onClose={() => setOpenModal(null)} onSave={handlePasswordReset} />
                )}
              </div>

              <p className="font-medium text-[#00000080] mb-8">
                Last Sign In: <span className="text-[#000000] ml-2 sm:ml-4">
                  {formatAppDateTime(securityData.last_signin, { emptyValue: "Never" })}
                </span>
              </p>

              <TwoStepVerificationSection
                userDisplayName={`${form.first_name || ""} ${form.last_name || ""}`.trim()}
              />

              <div>
                <div className="flex items-center justify-between gap-2 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">🛡️</span>
                    <h2 className="text-lg font-semibold">Trusted Devices</h2>
                  </div>
                  <div>
                    <button
                      onClick={() => setShowTrustedModal(true)}
                      className="bg-buttonRed hover:bg-buttonRed-hover text-white px-3 py-1 rounded-3xl text-sm font-medium transition-colors"
                    >
                      Manage
                    </button>
                  </div>
                </div>
                {securityData.devices && securityData.devices.length > 0 ? (
                  <div>
                    <div className="text-sm text-gray-500 mb-3">Most recent</div>
                    <div className="space-y-4">
                      {recentDevices.map((device) => {
                        const deviceInfo = formatDeviceInfo(device);
                        const addedDate = device.create_date || device.createDate || device.created_at || device.createDateTime || device.signin_date || device.signinDate || null;
                        return (
                          <div key={device.trusted_device_id} className="border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow">
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0">
                                {getDeviceTypeIcon(device.device_type, 20)}
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-sm font-medium text-gray-700 truncate">{deviceInfo.deviceName}</span>
                                    <span className="text-xs text-gray-500">{deviceInfo.osName}</span>
                                  </div>

                                  <div className="text-right ml-2 hidden sm:block">
                                    <div className="text-xs text-gray-500">Last activity</div>
                                    <div className="text-sm text-gray-700">{formatAppDateTime(device.activity_date, { emptyValue: 'Unknown' })}</div>
                                    {addedDate && (
                                      <div className="text-xs text-gray-400 mt-1">Added: <span className="text-gray-600">{formatAppDateTime(addedDate)}</span></div>
                                    )}
                                  </div>
                                </div>

                                <div className="flex items-center justify-between mt-1">
                                  <div className="flex items-center gap-3 text-sm text-gray-600 min-w-0">
                                    <span className="flex items-center gap-2 truncate">
                                      {getBrowserIcon(device.browser_type, 16)}
                                      <span className="truncate">{deviceInfo.browserName}</span>
                                    </span>
                                    <span className="truncate">{deviceInfo.location}</span>
                                    {(deviceInfo.countryCode || device.country) && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200 text-xs">
                                        <span className="font-semibold">{(deviceInfo.countryCode || device.country || '').toString().toUpperCase()}</span>
                                      </span>
                                    )}
                                  </div>

                                  <div className="sm:hidden text-xs text-gray-500 text-right">
                                    <div>{formatAppDateTime(device.activity_date, { emptyValue: 'Unknown' })}</div>
                                    {addedDate && (
                                      <div className="text-xs text-gray-400">Added: <span className="text-gray-600">{formatAppDateTime(addedDate)}</span></div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                    </div>

                    {totalDevices > recentDevices.length && (
                      <div className="text-sm text-gray-500 mt-2">Showing {recentDevices.length} of {totalDevices} devices</div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-gray-400 text-4xl mb-3">🔒</div>
                    <div className="text-gray-500 text-lg font-medium mb-2">No trusted devices found</div>
                    <div className="text-gray-400 text-sm">Devices you've logged in from will appear here</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {(selectedTab === "GENERAL INFORMATION" || selectedTab === "CONTACT" || selectedTab === "PAYROLL") && (
            <div className="ml-4 mb-2">
              <div className="flex items-center gap-4 mb-2">
                <button
                  onClick={handleCancel}
                  disabled={saving || !hasUnsavedChanges()}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-3xl text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  CANCEL
                </button>
                <button
                  onClick={onSave}
                  disabled={saving || !hasUnsavedChanges()}
                  className="bg-buttonRed hover:bg-buttonRed-hover text-white px-4 py-2 rounded-3xl text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {saving ? 'SAVING...' : 'SAVE'}
                </button>
              </div>
              {hasUnsavedChanges() && (
                <div className="text-sm text-amber-600 font-medium">
                  ⚠️ You have unsaved changes
                </div>
              )}
            </div>
          )}

        </div>
        {showTrustedModal && (
          <TrustedDevicesForm
            onClose={() => setShowTrustedModal(false)}
            devices={securityData.devices || []}
            onRemoveDevice={handleRemoveDevice}
          />
        )}
      </div>
    </Layout>)
};

export default MyAccount;
